import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { http, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown/loading; false = logged out
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await http.post("/auth/login", { email, password });
      if (data.token) localStorage.setItem("liq_token", data.token);
      setUser(data.user);
      // Login response doesn't include usage; refetch /me for full payload
      fetchMe();
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e?.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name) => {
    try {
      // If the user landed via a partner link, ?ref=CODE was persisted in
      // localStorage by the landing page — thread it through so the backend
      // can attach the referral to the new user record.
      const affiliate_ref = (typeof window !== "undefined"
        ? localStorage.getItem("liq_affiliate_ref")
        : null) || undefined;
      const { data } = await http.post("/auth/register", {
        email,
        password,
        name,
        affiliate_ref,
      });
      if (data.token) localStorage.setItem("liq_token", data.token);
      // Referral has been consumed — clear the cookie so we don't re-attach on
      // a future signup made from the same browser.
      if (affiliate_ref) localStorage.removeItem("liq_affiliate_ref");
      setUser(data.user);
      fetchMe();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e?.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await http.post("/auth/logout");
    } catch (err) {
      // Logout is local-only — clearing client state regardless of network outcome
      // is intentional. We still log so issues are debuggable.
      console.warn("Logout request failed (continuing to clear local session):", err);
    }
    localStorage.removeItem("liq_token");
    setUser(false);
  };

  const setTier = async (tier) => {
    try {
      const { data } = await http.put("/auth/tier", { tier });
      setUser((u) => (u ? { ...u, tier: data.tier, tier_model: data.tier_model } : u));
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e?.response?.data?.detail) || e.message };
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setTier, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
